import React, { useMemo, useRef } from 'react';

import { Typography } from 'antd';
import classNames from 'classnames';
import { format } from 'date-fns';
import styled from 'styled-components';

import StyledTable from 'components/Companies/table/StyledTable';
import { useAppSelector } from 'store/hook';

import UserManagementTableActions from './UserManagementTableActions';
import UserManagementTableInvitation from './UserManagementTableInvitation';
import { USER_MANAGEMENT_PAGE_SIZE } from '../constants';
import { UserManagementUserType } from '../types';
import { useUserManagementContext } from '../UserManagementContext';

const Container = styled.div`
  position: relative;
  display: flex;
  min-height: 0;
`;

const UsersStyledTable = styled(StyledTable)`
  &&&&&& {
    .ant-table-pagination.ant-pagination {
      padding-bottom: 12px;
    }
  }
`;

const Muted = styled.span`
  color: var(--text-secondary);
`;

const CommunitiesText = styled(Typography.Paragraph)`
  width: 100%;
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 0;
`;

const ExpandToggle = styled.div`
  color: var(--dark-blue);
  font-size: 12px;
`;

const DateText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const SubText = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const NameText = styled.span`
  font-family: var(--font-bold);
  font-size: 14px;
`;

const StatusText = styled.span`
  font-size: 14px;
  color: var(--text-secondary);

  &.active {
    color: var(--positive-green);
  }

  &.inActive {
    color: var(--inactive-orange);
  }
`;

const UserManagementTable = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { currentUser } = useAppSelector((state) => state.auth);
  const isAdminUser = Boolean(currentUser?.isSuperuser || currentUser?.isStaff);

  const {
    loading,
    items,
    totalItemsCount,
    filters,
    isMultipleSelection,
    selectedUserIds,
    selectedUsers,
    setPage,
    setSelectedUserIds,
    setSelectedUsers,
  } = useUserManagementContext();

  const formatDateTime = (value: string | null) => {
    if (!value) {
      return <span>-</span>;
    }

    return <DateText>{format(new Date(value), 'MMM dd, yyyy hh:mm a')}</DateText>;
  };

  const handleChange = (pagination: any, _filters: any, _sorter: any, extra: any) => {
    const { action } = extra;
    if (action === 'paginate') {
      setPage(pagination.current);
    }
  };

  const selectedUsersById = useMemo(
    () => new Map(selectedUsers.map((selectedUser) => [selectedUser.id, selectedUser])),
    [selectedUsers]
  );

  const syncSelectedUsers = (nextSelectedUserIds: number[], changedUsers: UserManagementUserType[]) => {
    const nextSelectedUsersById = new Map(selectedUsersById);

    changedUsers.forEach((user) => {
      if (nextSelectedUserIds.includes(user.id)) {
        nextSelectedUsersById.set(user.id, user);
      } else {
        nextSelectedUsersById.delete(user.id);
      }
    });

    setSelectedUserIds(nextSelectedUserIds);
    setSelectedUsers(
      nextSelectedUserIds.map((id) => nextSelectedUsersById.get(id)).filter(Boolean) as UserManagementUserType[]
    );
  };

  const columns: any[] = [
    {
      title: 'ID',
      dataIndex: 'auth_user_id',
      width: '4%',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      width: '10%',
      render: (value: any, record: any) => (
        <div>
          <NameText>{value}</NameText>
          <SubText>{record?.email || '-'}</SubText>
        </div>
      ),
    },
    {
      title: 'Position',
      width: '12%',
      render: (_value: any, record: any) => <span>{record?.position?.name || '-'}</span>,
    },
    {
      title: 'Company/Owner',
      width: '12%',
      visibility: isAdminUser ? 'visible' : 'hidden',
      render: (_value: any, record: any) => <span>{record?.company?.name || record?.holding?.name || '-'}</span>,
    },
    {
      title: 'Communities',
      width: '30%',
      render: (_value: any, record: any) => {
        const communities = record?.communities || [];
        const label =
          communities.length > 0 ? communities.map((community: any) => community.comunity_name).join(', ') : '-';
        return (
          <CommunitiesText
            ellipsis={{
              rows: 3,
              expandable: 'collapsible',
              symbol: (expanded: boolean) => <ExpandToggle>{expanded ? 'Show Less' : 'Show More'}</ExpandToggle>,
            }}
          >
            {label}
          </CommunitiesText>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      width: '5%',
      render: (value: boolean) => {
        return (
          <StatusText
            className={classNames({
              active: value,
              inActive: !value,
            })}
          >
            {value ? 'Active' : 'Inactive'}
          </StatusText>
        );
      },
    },
    {
      title: 'Last Seen',
      dataIndex: 'last_seen_at',
      width: '10%',
      render: (value: string | null) => formatDateTime(value),
    },
    {
      title: 'Last Invitation Sent',
      width: '10%',
      render: (_value: any, record: any) => <UserManagementTableInvitation item={record} />,
    },
    {
      title: '',
      with: '5%',
      render: (_value: any, record: any) => <UserManagementTableActions item={record} />,
    },
  ];

  return (
    <Container ref={containerRef} data-test-id="user-management-table-container">
      <UsersStyledTable
        data-test-id="user-management-table"
        columns={columns}
        dataSource={items}
        rowKey={(record: any) => record.id}
        onChange={handleChange}
        showSorterTooltip={false}
        loading={loading}
        rowSelection={
          isMultipleSelection
            ? {
                selectedRowKeys: selectedUserIds,
                preserveSelectedRowKeys: true,
                onSelect: (record: any, selected: boolean) => {
                  const nextSelectedUserIds = selected
                    ? Array.from(new Set([...selectedUserIds, record.id]))
                    : selectedUserIds.filter((id) => id !== record.id);

                  syncSelectedUsers(nextSelectedUserIds, [record]);
                },
                onSelectAll: (selected: boolean, _selectedRows: any[], changeRows: any[]) => {
                  const changedUserIds = changeRows.map((user) => user.id);
                  const nextSelectedUserIds = selected
                    ? Array.from(new Set([...selectedUserIds, ...changedUserIds]))
                    : selectedUserIds.filter((id) => !changedUserIds.includes(id));

                  syncSelectedUsers(nextSelectedUserIds, changeRows);
                },
              }
            : undefined
        }
        pagination={{
          size: 'small',
          position: ['bottomLeft'],
          showSizeChanger: false,
          current: filters.page,
          pageSize: USER_MANAGEMENT_PAGE_SIZE,
          total: totalItemsCount,
          showTotal: (total: number, range: number[]) => {
            const displayedItems = range[1] - range[0] + 1;
            return (
              <span>
                Showing {displayedItems} of {total} records
              </span>
            );
          },
        }}
      />
    </Container>
  );
};

export default UserManagementTable;
